import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/AppShell';
import { CopyButton } from '../components/CopyButton';
import { SectionTitle } from '../components/Feedback';
import { Mono } from '../components/Pills';
import { useAuth, useTheme } from '../state';

export function GuidePage(): JSX.Element {
  const { apiKey, user, organization } = useAuth();
  const { theme, toggle } = useTheme();

  const serverUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://justloop.vps.prakhya.id';

  const [activeTab, setActiveTab] = useState<'cli' | 'agent' | 'mcp' | 'tunnel' | 'commands'>('cli');

  const mcpCursorJson = JSON.stringify(
    {
      mcpServers: {
        justloop: {
          command: 'npx',
          args: ['-y', 'justloop', 'mcp'],
          env: {
            JUSTLOOP_URL: serverUrl,
            ...(apiKey ? { JUSTLOOP_API_KEY: apiKey } : { JUSTLOOP_API_KEY: 'your-api-key-here' }),
          },
        },
      },
    },
    null,
    2,
  );

  const mcpClaudeJson = JSON.stringify(
    {
      mcpServers: {
        justloop: {
          command: 'npx',
          args: ['-y', 'justloop', 'mcp'],
          env: {
            JUSTLOOP_URL: serverUrl,
            ...(apiKey ? { JUSTLOOP_API_KEY: apiKey } : { JUSTLOOP_API_KEY: 'your-api-key-here' }),
          },
        },
      },
    },
    null,
    2,
  );

  return (
    <div className="page guide-page">
      {/* If accessed standalone without AppShell */}
      {!apiKey && (
        <header className="guide-header-bar">
          <div className="guide-header-inner">
            <Link to="/" className="landing__logo">
              <span className="landing__logo-mark">J</span>
              <span className="landing__logo-text">Justloop</span>
            </Link>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn--small btn--secondary"
                onClick={toggle}
                title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? '☀ Light' : '☾ Dark'}
              </button>
              <Link to="/login" className="btn btn--small">
                Sign In to Dashboard →
              </Link>
            </div>
          </div>
        </header>
      )}

      <PageHeader
        title="Justloop Terminal & AI Agent Guide"
        subtitle="Step-by-step instructions to install the CLI, connect your terminal, onboard coding agents, and configure native MCP."
        actions={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <a
              href="https://www.npmjs.com/package/justloop"
              target="_blank"
              rel="noreferrer"
              className="btn btn--small btn--secondary"
            >
              npm package: justloop ↗
            </a>
            {apiKey ? (
              <Link to="/" className="btn btn--small">
                Open Projects →
              </Link>
            ) : (
              <Link to="/login" className="btn btn--small">
                Create Free Account →
              </Link>
            )}
          </div>
        }
      />

      {/* Interactive Tabs */}
      <div className="guide-tabs" role="tablist">
        <button
          type="button"
          className={`guide-tab${activeTab === 'cli' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('cli')}
        >
          1. Install &amp; Connect CLI
        </button>
        <button
          type="button"
          className={`guide-tab${activeTab === 'agent' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('agent')}
        >
          2. Onboard Coding Agents
        </button>
        <button
          type="button"
          className={`guide-tab${activeTab === 'mcp' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('mcp')}
        >
          3. Model Context Protocol (MCP)
        </button>
        <button
          type="button"
          className={`guide-tab${activeTab === 'tunnel' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('tunnel')}
        >
          4. Test Localhost via Tunnel
        </button>
        <button
          type="button"
          className={`guide-tab${activeTab === 'commands' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('commands')}
        >
          5. CLI Cheat Sheet
        </button>
      </div>

      {/* ------------------------------------------------ Tab 1: Install & Connect */}
      {activeTab === 'cli' && (
        <div className="guide-content">
          <section className="panel">
            <SectionTitle>Step 1: Install the Justloop CLI</SectionTitle>
            <p className="muted" style={{ margin: '0 0 16px' }}>
              The Justloop CLI is lightweight (~370 KB) and runs anywhere Node.js 20+ is installed.
            </p>

            <div className="guide-code-box">
              <div className="guide-code-header">
                <span>Option A: Global Installation (Recommended)</span>
                <CopyButton value="npm install -g justloop" />
              </div>
              <pre className="guide-code-pre">npm install -g justloop</pre>
            </div>

            <div className="guide-code-box" style={{ marginTop: '14px' }}>
              <div className="guide-code-header">
                <span>Option B: Zero-Install (Run instantly via npx)</span>
                <CopyButton value={`npx justloop doctor --server ${serverUrl}`} />
              </div>
              <pre className="guide-code-pre">npx justloop doctor --server {serverUrl}</pre>
            </div>

            <div style={{ marginTop: '16px' }}>
              <p className="muted" style={{ fontSize: '0.85rem' }}>
                Verify installation:
              </p>
              <div className="guide-code-box">
                <div className="guide-code-header">
                  <span>Check Version</span>
                  <CopyButton value="justloop --version" />
                </div>
                <pre className="guide-code-pre">justloop --version</pre>
              </div>
            </div>
          </section>

          <section className="panel" style={{ marginTop: '24px' }}>
            <SectionTitle>Step 2: Connect Your Terminal to the Justloop Server</SectionTitle>
            <p className="muted" style={{ margin: '0 0 16px' }}>
              Authenticate your terminal with your Justloop server. This saves your credentials to{' '}
              <code>~/.justloop/config.json</code>.
            </p>

            {apiKey && user ? (
              <div className="guide-user-badge">
                <span className="pill pill--pass">Active Session Detected</span>
                <p style={{ margin: '6px 0 0', fontSize: '0.88rem' }}>
                  Signed in as <strong>{user.email}</strong> ({organization?.name ?? 'Workspace'}).
                </p>
              </div>
            ) : null}

            <div className="guide-code-box" style={{ marginTop: '14px' }}>
              <div className="guide-code-header">
                <span>Method 1: One-Command Terminal Login</span>
                <CopyButton
                  value={`justloop auth login --server ${serverUrl} --email ${
                    user?.email ?? 'your-email@example.com'
                  } --password "your-password"`}
                />
              </div>
              <pre className="guide-code-pre">
{`justloop auth login \\
  --server ${serverUrl} \\
  --email ${user?.email ?? 'your-email@example.com'} \\
  --password "your-password"`}
              </pre>
            </div>

            <div className="guide-code-box" style={{ marginTop: '16px' }}>
              <div className="guide-code-header">
                <span>Method 2: Environment Variables (Ideal for CI/CD &amp; Docker)</span>
                <CopyButton
                  value={`export JUSTLOOP_URL=${serverUrl}\nexport JUSTLOOP_API_KEY=${apiKey ?? 'your-api-key'}`}
                />
              </div>
              <pre className="guide-code-pre">
{`export JUSTLOOP_URL=${serverUrl}
export JUSTLOOP_API_KEY=${apiKey ?? 'your-api-key'}`}
              </pre>
            </div>

            <div style={{ marginTop: '18px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>Step 3: Test Connection Health</h4>
              <div className="guide-code-box">
                <div className="guide-code-header">
                  <span>Run Doctor Healthcheck</span>
                  <CopyButton value={`justloop doctor --server ${serverUrl}`} />
                </div>
                <pre className="guide-code-pre">justloop doctor --server {serverUrl}</pre>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* ------------------------------------------- Tab 2: Onboard Coding Agents */}
      {activeTab === 'agent' && (
        <div className="guide-content">
          <section className="panel">
            <SectionTitle>Onboard Coding Agents in 1 Command</SectionTitle>
            <p className="muted" style={{ margin: '0 0 16px' }}>
              Run this command inside any project directory where your coding agent (Claude Code, Cursor,
              Windsurf, etc.) works. Justloop will detect which agent you use and install the native verification skill.
            </p>

            <div className="guide-code-box">
              <div className="guide-code-header">
                <span>Auto-Install Skill in Project</span>
                <CopyButton value={`justloop setup --server ${serverUrl}`} />
              </div>
              <pre className="guide-code-pre">justloop setup --server {serverUrl}</pre>
            </div>

            <h4 style={{ marginTop: '24px', marginBottom: '12px' }}>Where the Skill Lands per Agent</h4>
            <div className="table-wrap">
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th>Coding Agent</th>
                    <th>Skill Destination</th>
                    <th>Detection Marker</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Claude Code</strong></td>
                    <td><Mono>.claude/skills/justloop/SKILL.md</Mono></td>
                    <td><code>.claude/settings.json</code>, <code>CLAUDE.md</code></td>
                  </tr>
                  <tr>
                    <td><strong>Cursor</strong></td>
                    <td><Mono>.cursor/rules/justloop.mdc</Mono></td>
                    <td><code>.cursor/rules</code>, <code>.cursor/mcp.json</code></td>
                  </tr>
                  <tr>
                    <td><strong>Windsurf</strong></td>
                    <td><Mono>.windsurf/rules/justloop.md</Mono></td>
                    <td><code>.windsurf/</code>, <code>.windsurfrules</code></td>
                  </tr>
                  <tr>
                    <td><strong>Cline</strong></td>
                    <td><Mono>.clinerules/justloop.md</Mono></td>
                    <td><code>.clinerules</code> directory</td>
                  </tr>
                  <tr>
                    <td><strong>Roo Code</strong></td>
                    <td><Mono>.roomodes/justloop.md</Mono></td>
                    <td><code>.roomodes</code> directory</td>
                  </tr>
                  <tr>
                    <td><strong>GitHub Copilot</strong></td>
                    <td><Mono>.github/copilot-instructions.md</Mono></td>
                    <td><code>.github/copilot-instructions.md</code></td>
                  </tr>
                  <tr>
                    <td><strong>Codex CLI</strong></td>
                    <td><Mono>AGENTS.md.justloop</Mono></td>
                    <td><code>AGENTS.md</code>, <code>.codex/</code></td>
                  </tr>
                  <tr>
                    <td><strong>Pi Agent / DSH</strong></td>
                    <td><Mono>.agents/skills/justloop/SKILL.md</Mono></td>
                    <td><code>.agents/</code>, <code>.pi/</code>, <code>skills-lock.json</code></td>
                  </tr>
                  <tr>
                    <td><strong>Universal MCP</strong></td>
                    <td><Mono>.mcp.json</Mono> / <Mono>.cursor/mcp.json</Mono></td>
                    <td>Auto-configured during <code>justloop setup</code></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="notice notice--info" style={{ marginTop: '20px' }}>
              <div>
                <p className="notice__title">Automatic MCP Server Configuration Included</p>
                <p className="notice__message">
                  Running <code>justloop setup</code> automatically writes native <strong>.mcp.json</strong>, <strong>.cursor/mcp.json</strong>, and <strong>.pi/mcp.json</strong> files into your project, so your coding agent immediately receives both autonomous verification instructions and native MCP tools without manual JSON configuration!
                </p>
              </div>
            </div>

            <div className="notice notice--info" style={{ marginTop: '20px' }}>
              <div>
                <p className="notice__title">Why this matters</p>
                <p className="notice__message">
                  The skill instructs your coding agent to <strong>run tests and read failure bundles before</strong> reporting
                  that a task is completed. This stops the agent from guessing or claiming a bug is fixed when it isn't.
                </p>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* ----------------------------------------------------------- Tab 3: MCP */}
      {activeTab === 'mcp' && (
        <div className="guide-content">
          <section className="panel">
            <SectionTitle>Native Model Context Protocol (MCP) Server</SectionTitle>
            <p className="muted" style={{ margin: '0 0 16px' }}>
              Justloop ships a built-in stdio MCP server exposing <strong>9 verification tools</strong> directly
              to Claude Code, Cursor, and other MCP-compliant IDEs.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
              {/* Cursor Config */}
              <div className="panel" style={{ background: 'var(--bg-inset)', padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h4 style={{ margin: 0 }}>Cursor (<code>.cursor/mcp.json</code>)</h4>
                  <CopyButton value={mcpCursorJson} label="Copy JSON" />
                </div>
                <p className="muted" style={{ fontSize: '0.82rem', marginBottom: '10px' }}>
                  Save this in <code>.cursor/mcp.json</code> in your project:
                </p>
                <pre className="guide-code-pre" style={{ maxHeight: '240px' }}>
                  {mcpCursorJson}
                </pre>
              </div>

              {/* Claude Desktop / CLI Config */}
              <div className="panel" style={{ background: 'var(--bg-inset)', padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h4 style={{ margin: 0 }}>Claude Desktop / Code</h4>
                  <CopyButton value={mcpClaudeJson} label="Copy JSON" />
                </div>
                <p className="muted" style={{ fontSize: '0.82rem', marginBottom: '10px' }}>
                  Add to <code>claude_desktop_config.json</code>:
                </p>
                <pre className="guide-code-pre" style={{ maxHeight: '240px' }}>
                  {mcpClaudeJson}
                </pre>
              </div>
            </div>

            <h4 style={{ marginTop: '24px', marginBottom: '12px' }}>Native MCP Tools &amp; Prompts Available to Agents</h4>
            <div className="admin-plans-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <div className="admin-plan-card" style={{ textAlign: 'left', padding: '12px' }}>
                <div style={{ fontWeight: 700, color: 'var(--accent-text)', fontSize: '0.85rem' }}>verify_get_scenarios</div>
                <div className="muted" style={{ fontSize: '0.78rem' }}>Get full test scenarios &amp; sync to local workspace</div>
              </div>
              <div className="admin-plan-card" style={{ textAlign: 'left', padding: '12px' }}>
                <div style={{ fontWeight: 700, color: 'var(--accent-text)', fontSize: '0.85rem' }}>verify_generate_test_matrix</div>
                <div className="muted" style={{ fontSize: '0.78rem' }}>Generate 4-quadrant test matrix (Positive, Negative, Edge)</div>
              </div>
              <div className="admin-plan-card" style={{ textAlign: 'left', padding: '12px' }}>
                <div style={{ fontWeight: 700, color: 'var(--accent-text)', fontSize: '0.85rem' }}>verify_get_personas</div>
                <div className="muted" style={{ fontSize: '0.78rem' }}>Get durable test account personas &amp; variable tokens</div>
              </div>
              <div className="admin-plan-card" style={{ textAlign: 'left', padding: '12px' }}>
                <div style={{ fontWeight: 700, color: 'var(--accent-text)', fontSize: '0.85rem' }}>verify_run_test</div>
                <div className="muted" style={{ fontSize: '0.78rem' }}>Execute test on real Chromium</div>
              </div>
              <div className="admin-plan-card" style={{ textAlign: 'left', padding: '12px' }}>
                <div style={{ fontWeight: 700, color: 'var(--accent-text)', fontSize: '0.85rem' }}>verify_get_failure</div>
                <div className="muted" style={{ fontSize: '0.78rem' }}>Fetch full failure bundle, screenshot &amp; DOM snapshot</div>
              </div>
              <div className="admin-plan-card" style={{ textAlign: 'left', padding: '12px' }}>
                <div style={{ fontWeight: 700, color: 'var(--accent-text)', fontSize: '0.85rem' }}>verify_rerun</div>
                <div className="muted" style={{ fontSize: '0.78rem' }}>Replay test after code fix</div>
              </div>
              <div className="admin-plan-card" style={{ textAlign: 'left', padding: '12px' }}>
                <div style={{ fontWeight: 700, color: 'var(--accent-text)', fontSize: '0.85rem' }}>verify_lint_plan</div>
                <div className="muted" style={{ fontSize: '0.78rem' }}>Validate plan syntax offline</div>
              </div>
              <div className="admin-plan-card" style={{ textAlign: 'left', padding: '12px' }}>
                <div style={{ fontWeight: 700, color: 'var(--accent-text)', fontSize: '0.85rem' }}>verify_list_tests</div>
                <div className="muted" style={{ fontSize: '0.78rem' }}>List tests in project</div>
              </div>
              <div className="admin-plan-card" style={{ textAlign: 'left', padding: '12px' }}>
                <div style={{ fontWeight: 700, color: 'var(--accent-text)', fontSize: '0.85rem' }}>verify_create_test / upsert</div>
                <div className="muted" style={{ fontSize: '0.78rem' }}>Create or update durable test cases</div>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* -------------------------------------------------------- Tab 4: Tunnel */}
      {activeTab === 'tunnel' && (
        <div className="guide-content">
          <section className="panel">
            <SectionTitle>Testing Apps on Your Laptop (localhost) via Reverse Tunnel</SectionTitle>
            <p className="muted" style={{ margin: '0 0 16px' }}>
              When the browser runner runs on the server (like <code>{serverUrl}</code>), it cannot reach{' '}
              <code>http://localhost:3000</code> on your laptop directly. The Justloop Reverse Tunnel creates
              a secure WebSocket connection between your machine and the server.
            </p>

            <div className="guide-code-box">
              <div className="guide-code-header">
                <span>Terminal 1: Start the Reverse Tunnel</span>
                <CopyButton value={`justloop tunnel start --port 3000 --server ${serverUrl}`} />
              </div>
              <pre className="guide-code-pre">justloop tunnel start --port 3000 --server {serverUrl}</pre>
            </div>

            <div style={{ marginTop: '16px' }}>
              <p className="muted" style={{ fontSize: '0.85rem' }}>
                The command will connect and print your active client ID:
              </p>
              <pre className="guide-code-pre" style={{ color: 'var(--pass)' }}>
{`✔ tunnel tun_4f2a91c8 connected
  serving http://127.0.0.1:3000`}
              </pre>
            </div>

            <div className="guide-code-box" style={{ marginTop: '16px' }}>
              <div className="guide-code-header">
                <span>Terminal 2: Run Tests Against Your Laptop App</span>
                <CopyButton value="justloop test run <testId> --tunnel-client tun_4f2a91c8 --wait" />
              </div>
              <pre className="guide-code-pre">justloop test run &lt;testId&gt; --tunnel-client tun_4f2a91c8 --wait</pre>
            </div>
          </section>
        </div>
      )}

      {/* ---------------------------------------------------- Tab 5: Commands */}
      {activeTab === 'commands' && (
        <div className="guide-content">
          <section className="panel">
            <SectionTitle>CLI Command Reference Cheat Sheet</SectionTitle>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Command</th>
                    <th>Description</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>justloop setup</code></td>
                    <td>Auto-install verification skill &amp; native .mcp.json into Claude Code, Pi Agent, Cursor, Windsurf, or AGENTS.md</td>
                    <td><CopyButton value={`justloop setup --server ${serverUrl}`} /></td>
                  </tr>
                  <tr>
                    <td><code>justloop setup --scope user</code></td>
                    <td>Install skill and MCP globally across your entire machine (~/.claude, ~/.pi, ~/.cursor)</td>
                    <td><CopyButton value={`justloop setup --server ${serverUrl} --scope user`} /></td>
                  </tr>
                  <tr>
                    <td><code>justloop update</code></td>
                    <td>Auto-update CLI package via npm and refresh all installed agent skills &amp; MCP configs</td>
                    <td><CopyButton value="justloop update" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop update --check</code></td>
                    <td>Check for new CLI package releases and server versions without installing</td>
                    <td><CopyButton value="justloop update --check" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop doctor</code></td>
                    <td>Check Node.js, server reachability, API key, browser driver, and agent skills status</td>
                    <td><CopyButton value={`justloop doctor --server ${serverUrl}`} /></td>
                  </tr>
                  <tr>
                    <td><code>justloop auth login</code></td>
                    <td>Authenticate terminal interactively and store credentials in local profile (~/.justloop)</td>
                    <td><CopyButton value={`justloop auth login --server ${serverUrl} --email your-email@example.com`} /></td>
                  </tr>
                  <tr>
                    <td><code>justloop auth status</code></td>
                    <td>Show active user, organization, API key scopes, and remaining credit balance</td>
                    <td><CopyButton value="justloop auth status" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop project list</code></td>
                    <td>List all projects in your organization with latest pass rates and run summaries</td>
                    <td><CopyButton value="justloop project list" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop project create</code></td>
                    <td>Create a new project pointing to a web app or tunnel port</td>
                    <td><CopyButton value={`justloop project create --name "My app" --url http://localhost:3000`} /></td>
                  </tr>
                  <tr>
                    <td><code>justloop project delete &lt;id&gt; --confirm</code></td>
                    <td>Permanently delete a project, its tests, environments, and run history</td>
                    <td><CopyButton value="justloop project delete <projectId> --confirm" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop test list</code></td>
                    <td>List tests in project with pass rate, tags, and latest execution status</td>
                    <td><CopyButton value="justloop test list" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop test run &lt;id&gt; --wait</code></td>
                    <td>Execute a test on real Chromium and wait for exit code verdict (0 = pass, 1 = fail)</td>
                    <td><CopyButton value="justloop test run <testId> --wait" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop test failure get &lt;id&gt;</code></td>
                    <td>Fetch root cause, video, screenshot, and suggested fix from single snapshotId</td>
                    <td><CopyButton value="justloop test failure get <testId>" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop test rerun &lt;id&gt; --wait</code></td>
                    <td>Re-execute test after code fix and compound durable suite coverage</td>
                    <td><CopyButton value="justloop test rerun <testId> --wait" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop test lint plan.json</code></td>
                    <td>Offline syntax check of a plan file (0 credits, no network required)</td>
                    <td><CopyButton value="justloop test lint plan.json" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop scenario pull</code></td>
                    <td>Pull all test scenarios into organized local files (justloop-scenarios/&lt;feature&gt;/...)</td>
                    <td><CopyButton value="justloop scenario pull" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop scenario list --local</code></td>
                    <td>List all local scenario files grouped by feature and branch offline</td>
                    <td><CopyButton value="justloop scenario list --local" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop ci --comment-file pr.md</code></td>
                    <td>Run CI suite and output formatted GitHub PR markdown matrix report</td>
                    <td><CopyButton value="justloop ci --all --comment-file pr-summary.md" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop test code &lt;id&gt;</code></td>
                    <td>Export standalone Playwright TypeScript (.spec.ts) test source code</td>
                    <td><CopyButton value="justloop test code <testId>" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop tunnel start --port &lt;p&gt;</code></td>
                    <td>Open reverse WebSocket tunnel so the cloud runner can test your local app</td>
                    <td><CopyButton value={`justloop tunnel start --port 3000 --server ${serverUrl}`} /></td>
                  </tr>
                  <tr>
                    <td><code>justloop agent list</code></td>
                    <td>Detect which coding agents are configured in the current repository</td>
                    <td><CopyButton value="justloop agent list" /></td>
                  </tr>
                  <tr>
                    <td><code>justloop mcp</code></td>
                    <td>Run stdio Model Context Protocol server exposing verification tools and prompts to coding agents</td>
                    <td><CopyButton value="justloop mcp" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
